import { AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ThemeService } from '../../services/theme.service';
import { FooterComponent } from '../footer/footer.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslationSyncService } from '../../services/translation-sync.service';
import { ScrollStateService } from '../../services/scroll-state.service';
import { HideShellOnScrollDirective } from '../../directives/hide-shell-on-scroll.directive';
import { HelperService } from '../../services/helper.service';

@Component({
  selector: 'app-portfolio',
  imports: [CommonModule, FooterComponent, TranslateModule, HideShellOnScrollDirective],
  templateUrl: './portfolio.component.html',
  styleUrl: './portfolio.component.css',
  encapsulation: ViewEncapsulation.None // wird benoetigt fuer dark container
})
export class PortfolioComponent implements OnInit, AfterViewInit, AfterViewChecked {


  @ViewChild('containerPortfolio', { static: false }) containerRef!: ElementRef<HTMLElement>;
  @Output() toggleImprint = new EventEmitter<void>();
  @Output() togglePrivacy = new EventEmitter<void>();


  tabs: { label: string; icon: string }[] = [
    { label: 'PORTFOLIO.TABS.WORK', icon: 'bx bx-rocket' },
    { label: 'PORTFOLIO.TABS.SERVICE', icon: 'bx bx-cog' }]
    ;

  readonly previewLinks = [
    '/',
    '/'
  ];

  activeIndex: number = 0;
  lastIndex: number = 0;
  private darkContainer!: HTMLElement;
  isDarkMode: boolean;

  tabContent: any = { work: [], service: [] };

  constructor(
    private renderer: Renderer2
    , private themeService: ThemeService
    , private sync: TranslationSyncService
    , private translate: TranslateService
    , private scroll: ScrollStateService
    , private helper: HelperService) { }

  ngOnInit(): void {
    this.themeService.isDarkMode$.subscribe(mode => {
      this.isDarkMode = mode;
      Promise.resolve().then(() => {
        const triggeredByToggle = this.themeService.wasThemeToggled();
        this.updateTheme(triggeredByToggle);
      });
    });

    // Content laden aus i18n dateien
    this.translate.get('PORTFOLIO.CONTENT').subscribe((content: any) => {

      // Content kommt jetzt als echtes JS-Objekt
      this.tabContent.work = content.WORK;
      this.tabContent.service = content.SERVICE;

      this.updateDarkContainerTab();
      setTimeout(() => {
        this.registerAll(this.containerRef.nativeElement);
      }, 100);
    });
  }

  ngAfterViewInit(): void {
    const container = this.containerRef.nativeElement;
    this.darkContainer = document.getElementById('dark-container-portfolio') as HTMLElement;
    this.registerAll(container);
    this.renderer.removeClass(this.darkContainer, 'active');

    this.updateDarkContainerTab(),

      Promise.resolve().then(() => {
        const triggeredByToggle = this.themeService.wasThemeToggled();
        this.updateTheme(triggeredByToggle);
      });

  }

  ngAfterViewChecked(): void {
    if (this.activeIndex !== this.lastIndex) {
      this.lastIndex = this.activeIndex;
      this.registerAll(this.containerRef.nativeElement);
    }
  }

  setActive(index: number): void {
    this.activeIndex = index;
    this.syncDarkContainerActiveState();
    setTimeout(() => {
      const key = this.activeIndex === 0 ? 'portfolio-work-dark' : 'portfolio-service-dark';

      this.scroll.bindShellHideOnScroll(
        '#dark-container-portfolio .tab-grid.portfolio-box.active',
        key,
        { dock: true, nav: false, footer: false }
      );
    }, 100);

  }

  nextActive(): void {
    this.activeIndex = (this.activeIndex + 1) % this.tabs.length;
  }

  private updateTheme(triggeredByToggle: boolean): void {
    if (!this.darkContainer || !this.containerRef?.nativeElement) {
      return;
    }

    this.updateScrollHeight();
    this.themeService.toggleThemeMode(this.isDarkMode, this.renderer, this.containerRef.nativeElement, this.darkContainer);
  }

  private updateDarkContainerTab(): void {
    const container = document.getElementById('dark-container-portfolio');
    if (!container) return;

    // 🔸 1. Tab-Leiste exakt nachbilden
    const tabBox = container.querySelector('.tab-box');
    if (tabBox) {
      tabBox.innerHTML = '';
      this.tabs.forEach(({ label, icon }, i) => {
        const tabDiv = document.createElement('div');
        tabDiv.className = `tab-list portfolio-list${i === this.activeIndex ? ' active' : ''}`;

        // Icon-Element erzeugen
        const iconEl = document.createElement('i');
        iconEl.className = `${icon} tab-icon`; // z. B. 'bx bxl-trip-advisor tab-icon'
        tabDiv.appendChild(iconEl);


        const h3 = document.createElement('h3');
        h3.className = 'tab-label';
        h3.setAttribute('data-translate', label);
        h3.textContent = label;
        tabDiv.appendChild(h3);
        tabDiv.addEventListener('click', () => this.setActive(i));
        tabBox.appendChild(tabDiv);
      });
    }

    // 🔸 2. Tab-Wrapper aufbauen mit Work- und Service-Boxen
    const tabWrapper = container.querySelector('.tab-wrapper');
    if (!tabWrapper) return;
    tabWrapper.innerHTML = '';

    // 🟠 Work-Box (analog zu deinem Template)
    const workBox = document.createElement('div');
    workBox.className = `tab-grid portfolio-box work${this.activeIndex === 0 ? ' active' : ''}`;


    workBox.innerHTML = this.tabContent.work.map((work, i) => `
        <div class="tab-item portfolio-item">
          <div class="work-img">
            <img src="${work.IMG_SRC}" alt="">
          </div>
          <div class="work-info">
           <h4 data-translate="PORTFOLIO.CONTENT.WORK.${i}.TITLE">${this.helper.escapeHTML(work.TITLE)}</h4>
           <p data-translate="PORTFOLIO.CONTENT.WORK.${i}.DESCRIPTION">${this.helper.escapeHTML(work.DESCRIPTION)}</p>
            <div class="tech-used">
              <p data-translate="PORTFOLIO.CONTENT.WORK.${i}.PROGRAM_LANGS">${this.helper.escapeHTML(work.PROGRAM_LANGS)}</p>
            </div>
            <div class="portfolio-icon work-icon">
              <a href="${this.previewLinks[i]}" target="_blank">
                <i class="bx bx-arrow-back"></i>
                <span class="tooltip">Preview</span>
              </a>
            </div>
          </div>
        </div>
      `).join('');
    tabWrapper.appendChild(workBox);

    // 🟢 Service-Box (analog zu deinem Template)
    const serviceBox = document.createElement('div');
    serviceBox.className = `tab-grid portfolio-box service${this.activeIndex === 1 ? ' active' : ''}`;
    serviceBox.innerHTML = this.tabContent.service.map((service, i) => `
        <div class="tab-item portfolio-item">
          <div class="portfolio-icon service-icon">
            <i class="${service.ICON}"></i>

          </div>
          <h4 data-translate="PORTFOLIO.CONTENT.SERVICE.${i}.CATEGORY">${this.helper.escapeHTML(service.CATEGORY)}</h4>
          <p data-translate="PORTFOLIO.CONTENT.SERVICE.${i}.DESCRIPTION">${this.helper.escapeHTML(service.DESCRIPTION)}</p>
        </div>
      `).join('');
    tabWrapper.appendChild(serviceBox);

    setTimeout(() => {
      this.registerAll(container);
      const key = this.activeIndex === 0 ? 'portfolio-work-dark' : 'portfolio-service-dark';

      this.scroll.bindShellHideOnScroll(
        '#dark-container-portfolio .tab-grid.portfolio-box.active',
        key,
        { dock: true, nav: false, footer: false }
      );

    }, 100);
  }


  private syncDarkContainerActiveState(): void {
    const container = document.getElementById('dark-container-portfolio');
    if (!container) return;

    const tabButtons = container.querySelectorAll('.tab-box .tab-list');
    tabButtons.forEach((btn, i) => {
      btn.classList.toggle('active', i === this.activeIndex);
    });

    const contentBoxes = container.querySelectorAll('.tab-wrapper .portfolio-box');
    contentBoxes.forEach((box, i) => {
      box.classList.toggle('active', i === this.activeIndex);
    });
  }

  private registerAll(root: HTMLElement) {
    const elems = Array.from(root.querySelectorAll<HTMLElement>('[data-translate]'));
    elems.forEach(el => {
      const key = el.getAttribute('data-translate')!;
      this.sync.register(el, key);
    });
  }

  private updateScrollHeight(): void {
    const scrollContainer = document.querySelector('#containerPortfolio .tab-grid.portfolio-box.active');
    const darkScrollContainer = document.querySelector('#dark-container-portfolio .tab-grid.portfolio-box.active');

    if (this.isDarkMode) {
      const scrollPos = scrollContainer ? scrollContainer.scrollTop : 0;
      darkScrollContainer.scrollTop = scrollPos;

    } else {
      const scrollPos = darkScrollContainer ? darkScrollContainer.scrollTop : 0;
      scrollContainer.scrollTop = scrollPos;
    }
  }




}
